import {noop} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import invariant from 'invariant'

import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {closeModal} from '../../../redux/features/modal'

import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as dynamicProductionTimeApi from '../../../services/api/dynamic-production-time'
import {formDataToDynamicProductionTimeRequest} from '../../forms/dynamic-production-time/utils'
import {toValidationSchema} from '../../utils/form'

import {DynamicProductionTime, ModalMeta} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import Headline from '../../../../../commons/src/components/headline'
import Modal from '../../../../../commons/src/components/modal'

import DynamicProductionTimeForm, {
  SingleFormData,
  initialValues,
  validationSchema,
} from '../../forms/dynamic-production-time/form'

type Props = {
  meta: ModalMeta
  onDone?: (dynamicProductionTime: DynamicProductionTime) => void
}

const CreateDynamicProductionTimeModal = ({meta, onDone = noop}: Props) => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const formik = useFormik<SingleFormData>({
    initialValues,
    validationSchema: toValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const dynamicProductionTimeRequest = formDataToDynamicProductionTimeRequest(values, provider)
      try {
        const createdDynamicProductionTime = await dynamicProductionTimeApi.create(
          dynamicProductionTimeRequest
        )

        dispatch(closeModal())
        onDone(createdDynamicProductionTime)
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Create new dynamic production time" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={
              formik.isSubmitting ||
              (formik.submitCount > 0 && Object.keys(formik.errors).length > 0)
            }
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <DynamicProductionTimeForm formik={formik} />
    </Modal>
  )
}

export default CreateDynamicProductionTimeModal
